import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Post-Google OAuth callback. Lives at any URL where #session_id=… appears in
 * the hash (App.js intercepts that synchronously and renders this component).
 *
 * Defensive design: any failure here is the dreaded "login loop", so we make
 * three layers of recovery before falling back to /login:
 *   1. Try POST /api/auth/session with the hash session_id.
 *   2. On failure, try GET /api/auth/me — covers the race where the cookie
 *      WAS set successfully but the response promise rejected (CORS quirks,
 *      browser extensions, etc.) so we never saw the user payload.
 *   3. As an extra safety net, the same /me fallback runs if there's no
 *      session_id in the hash at all (e.g. user manually pasted a /dashboard URL
 *      while still authenticated from a previous tab).
 *
 * On any final failure, surface the SERVER's error message (not a generic
 * "Authentication failed") so the user / our logs can pinpoint what broke.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // StrictMode double-mount guard — a single session_id can only be exchanged once.
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const routeForUser = (user) => {
      const dest =
          user.role === 'admin'      ? '/admin'
        : user.role === 'technician' ? '/tech'
        : user.role === 'accountant' ? '/admin'
        : '/dashboard';
      // Strip the #session_id= fragment so back/forward navigation can't re-trigger
      // AuthCallback with a now-consumed (and invalid) session_id.
      window.history.replaceState(null, '', window.location.pathname);
      navigate(dest, { state: { user }, replace: true });
      toast.success(`Welcome${user?.name ? `, ${user.name}` : ''}!`);
    };

    /** Best-effort recovery: try the cookie that may already be present. */
    const tryCookieAuth = async () => {
      try {
        const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (me?.data?.role) {
          routeForUser(me.data);
          return true;
        }
      } catch (_e) {
        /* fall through */
      }
      return false;
    };

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      // No fragment? Maybe the cookie's already valid (returning user, fresh tab).
      if (!sessionIdMatch) {
        if (!(await tryCookieAuth())) {
          navigate('/login', { replace: true });
        }
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        routeForUser(response.data);
      } catch (error) {
        console.error('Auth: /api/auth/session failed:', error);

        // Defensive recovery: if the cookie was set despite the response failing
        // (uncommon, but happens with some CORS/extension setups), /me will succeed.
        if (await tryCookieAuth()) return;

        const detail = error.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (Array.isArray(detail) ? detail.map((d) => d.msg).join('; ') : null)
          || error.message
          || 'Authentication failed. Please try again.';
        toast.error(msg);

        // Strip the consumed session_id before sending the user back to /login,
        // otherwise a refresh would re-trigger this component on the same dead id.
        window.history.replaceState(null, '', '/login');
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground font-mono">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
